import { ILegalClient } from '../../../../../Interfaces/ILegalClient';
import { createLegalClientAsset } from '../../../../../Interfaces/ILegalClientAsset';
import { ILegalClientAssetRoleId } from '../../../../../Interfaces/ILegalClientAssetRoleId';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  getEmptyInheritorMember,
  IMemberInheritor,
} from '../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberInheritor';
import {
  getEmptyInheritanceProvisionRelationship,
  IInheritanceProvisionRelationship,
  isInheritanceProvisionRelationship,
} from '../../../../../Interfaces/ILegalTransactionRelationship/Relationships/InheritanceProvision/ILegalTransactionRelationshipInheritanceProvision';
import { createRelationshipAsset } from '../../../../../Interfaces/IRelationshipAsset';
import { legalClientStore } from '../../generic/legalClientStore';
import { isTestamentRelationship, present } from '../../generic/utilities';
import { ITestamentAssetOwnerAndProvisions } from '../components/TestamentAssetsDialog';
import { IAssetProvisions } from '../components/TestamentProvisionsView';

function _mapProvisionsToMembers(
  legalClientArray: ILegalClient[],
  provisions: IAssetProvisions,
  relationship: IInheritanceProvisionRelationship
): IMemberInheritor[] {
  const updatedMembers = provisions.legalClientProvisions.map(lp => {
    const legalClient = legalClientArray.find(
      lc => lc.idLegalClient === lp.idLegalClient
    );
    if (!legalClient) {
      throw new Error('Legal client not found for provision member.');
    }
    const existingMember = relationship.Members.find(
      m => m.idLegalClient === lp.idLegalClient
    );
    if (existingMember) {
      return {
        ...existingMember,
        data: {
          ...existingMember.data,
          provisionNotes: lp.provisionNote,
        } as IMemberInheritor['data'],
      };
    }
    const member = getEmptyInheritorMember(
      legalClient,
      relationship.idRelationship
    );
    const memberWithInheritorData: IMemberInheritor = {
      ...member,
      data: {
        ...member.data,
        provisionNotes: lp.provisionNote,
      },
    };
    return memberWithInheritorData;
  });

  return updatedMembers;
}

function handleUpdateRelationships({
  asset,
  provisions,
  relationshipArray,
}: ITestamentAssetOwnerAndProvisions & {
  relationshipArray: ILegalTransactionRelationship[];
}) {
  const { legalClientArray } = legalClientStore.getState();

  const testamentRelationship = relationshipArray.find(isTestamentRelationship);
  const inheritanceProvisionRelationship = relationshipArray
    .filter(isInheritanceProvisionRelationship)
    .find(rel => rel.AssetArray.find(a => a.idAsset === asset.idAsset));

  const updatedRelationhips: ILegalTransactionRelationship[] = [];

  if (!testamentRelationship) {
    throw new Error('Testament relationship not found when saving asset.');
  }

  const updatedTestamentRelationship: ILegalTransactionRelationship = {
    ...testamentRelationship,
    AssetArray: [
      ...testamentRelationship.AssetArray,
      createRelationshipAsset(
        testamentRelationship.idRelationship,
        asset.idAsset
      ),
    ],
  };

  updatedRelationhips.push(updatedTestamentRelationship);

  if (inheritanceProvisionRelationship) {
    const updatedRelationship: IInheritanceProvisionRelationship = {
      ...inheritanceProvisionRelationship,
      AssetArray: [
        createRelationshipAsset(
          inheritanceProvisionRelationship.idRelationship,
          asset.idAsset
        ),
      ],
      Members: _mapProvisionsToMembers(
        legalClientArray,
        provisions,
        inheritanceProvisionRelationship
      ),
      data: {
        provisionNotes: provisions.otherProvisionsNotes || '',
      },
    };
    updatedRelationhips.push(updatedRelationship);
  } else {
    const newRelationship = getEmptyInheritanceProvisionRelationship();

    const updatedRelationship: ILegalTransactionRelationship = {
      ...newRelationship,
      Parent_idRelationship: testamentRelationship.idRelationship,
      Members: _mapProvisionsToMembers(
        legalClientArray,
        provisions,
        newRelationship
      ),
      AssetArray: [asset],
      data: {
        provisionNotes: provisions.otherProvisionsNotes || '',
      },
    };

    updatedRelationhips.push(updatedRelationship);
  }

  return updatedRelationhips;
}

function handleUpdateLegalClients({
  asset,
  ownerIds,
  ownerShares,
}: ITestamentAssetOwnerAndProvisions): ILegalClient[] {
  const { getLegalClient } = legalClientStore.getState();

  const updatedLegalClients = ownerIds
    .map(ownerId => {
      const legalClient = getLegalClient(ownerId);

      if (!legalClient) return;

      const existingAsset = legalClient?.AssetArray.find(
        la => la.idAsset === asset.idAsset
      );

      if (existingAsset) {
        const updatedLegalClient: ILegalClient = {
          ...legalClient,
          AssetArray: legalClient.AssetArray.map(legalClientAsset =>
            legalClientAsset.idAsset === asset.idAsset
              ? createLegalClientAsset(
                  ownerId,
                  asset.idAsset,
                  ILegalClientAssetRoleId.Owner,
                  ownerShares[ownerId]
                )
              : legalClientAsset
          ),
        };
        return updatedLegalClient;
      }

      const updatedLegalClient: ILegalClient = {
        ...legalClient,
        AssetArray: [
          ...legalClient.AssetArray,
          createLegalClientAsset(
            ownerId,
            asset.idAsset,
            ILegalClientAssetRoleId.Owner,
            ownerShares[ownerId]
          ),
        ],
      };
      return updatedLegalClient;
    })
    .filter(present);

  return updatedLegalClients;
}

const testamentAssetsDataService = {
  handleUpdateRelationships,
  handleUpdateLegalClients,
};

export default testamentAssetsDataService;
